import {Component, MeshComponent, Object3D, WonderlandEngine} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {TextFade} from './text-fade.js';

/**
 * blink
 */
export class Blink extends Component {
    static TypeName = 'blink';

    /**
     * The number of seconds the blink sphere is visible.
     */
    @property.float(1)
    blinkTime = 1;

    /**
     * The sphere that is used to indicate the blink.
     */
    @property.object()
    blinkSphere?: Object3D;

    private meshComponent?: MeshComponent;
    private textFadeComponent?: TextFade;
    private callbackDuringBlink?: CallableFunction | null;
    private callbackAfterBlink?: CallableFunction | null;
    private blinkInProgress = false;
    private fadedInOnly = false;
    private rej?: CallableFunction;
    private res?: CallableFunction;

    start(): void {
        if (!this.blinkSphere) {
            throw new Error('Blink: blinkSphere object is required with blink component');
        }
        const tempMeshComponent = this.blinkSphere.getComponent(MeshComponent);
        if (!tempMeshComponent) {
            throw new Error('Blink: blinkSphere object needs to have a MeshComponent');
        }
        this.meshComponent = tempMeshComponent;

        const tempTextFade = this.blinkSphere.getComponent(TextFade);
        if (!tempTextFade) {
            throw new Error(
                `blink(${this.object.name}): blinkSphere does not have a TextFade`
            );
        }
        this.textFadeComponent = tempTextFade;
    }

    onActivate(): void {
        this.textFadeComponent!.fadedIn.add(this.onFadedInHandler);
        this.textFadeComponent!.fadedOut.add(this.onFadedOutHandler);
    }

    onFadedInHandler = () => {
        this.onFadedIn().catch(console.error);
    };
    onFadedOutHandler = () => {
        this.onFadedOut().catch(console.error);
    };

    onDeactivate(): void {
        this.callbackAfterBlink = null;
        this.callbackDuringBlink = null;
        this.textFadeComponent!.fadedIn.remove(this.onFadedInHandler);
        this.textFadeComponent!.fadedOut.remove(this.onFadedOutHandler);
    }

    private async onFadedIn() {
        if (this.callbackDuringBlink) {
            await this.callbackDuringBlink();
        }

        if (!this.fadedInOnly) {
            this.textFadeComponent!.fadeOut();
        } else {

            this.blinkInProgress = false;

            /* Make sure callbacks are allowed to blink again */
            const cb = this.callbackAfterBlink;
            this.callbackAfterBlink = null;
            this.callbackDuringBlink = null;

            if(this.res) {
                await this.res();
            }

            if (cb) {
                await cb();
            }
        }
    }

    private async onFadedOut() {
        this.meshComponent!.active = false;

        /* Make sure callbacks are allowed to blink again */
        const cb = this.callbackAfterBlink;
        this.callbackDuringBlink = null;
        this.callbackAfterBlink = null;
        this.blinkInProgress = false;

        if(this.res) {
            this.res();
        }

        if (cb) {
            await cb();
        }
    }


    /**
     * Starts the blinking animation by fading to black and back to transparent.
     * @param duringBlink - A function to be called during the blink animation, when the screen is black.
     * @param afterBlink - A function to be called after the blink animation has finished.
     */
    async startBlink(
        duringBlink: CallableFunction | null = null,
        afterBlink: CallableFunction | null = null,
        fadeOutOnly = false,
        onetimeBlinkDelay: number | null = null,
        fadeInOnly = false
    ) : Promise<void> {
        if (this.blinkInProgress) {
            console.warn('Blink: blinking already in progress.');
            /* Avoid breaking the game by not calling these callbacks */
            if(duringBlink) {
                duringBlink();
            }
            if(afterBlink) {
                afterBlink();
            }
            return;
        }

        this.blinkInProgress = true;
        this.callbackDuringBlink = duringBlink;
        this.callbackAfterBlink = afterBlink;
        this.fadedInOnly = fadeInOnly;
        this.meshComponent!.active = true;
        const textFade = this.blinkSphere!.getComponent(TextFade)!;
        return new Promise((res, rej) => {
            this.res = res;
            this.rej = rej;
            if (fadeOutOnly) {
                setTimeout(() => {
                    this.textFadeComponent!.fadeOut();
                }, (onetimeBlinkDelay ?? this.blinkTime) * 1000);
            } else {
                textFade.fadeIn();
            }
        });
    }
}
